<template>
  <section class="view-stack">
    <section class="profile-layout">
      <article class="dashboard-panel profile-card">
        <div class="avatar-ring">
          <i class="el-icon-user-solid"></i>
        </div>
        <h2>{{ profile.email || '未登录用户' }}</h2>
        <p>账户资料只读展示，邮箱当前不支持修改。</p>
        <el-tag :type="profile.status === 1 ? 'success' : 'info'">
          {{ profile.status === 1 ? '账户正常' : '账户禁用' }}
        </el-tag>
      </article>

      <article class="dashboard-panel">
        <div class="panel-heading">
          <div>
            <h2>个人资料</h2>
            <p>这里展示当前账号的基础资料和最近登录信息。</p>
          </div>
          <el-button icon="el-icon-refresh" round :loading="loadingProfile" @click="loadProfile">
            刷新
          </el-button>
        </div>
        <el-form label-width="110px" class="profile-form">
          <el-form-item label="邮箱">
            <el-input :value="profile.email" disabled></el-input>
          </el-form-item>
          <el-form-item label="注册时间">
            <el-input :value="formatDateTime(profile.registerTime)" disabled></el-input>
          </el-form-item>
          <el-form-item label="最近登录时间">
            <el-input :value="formatDateTime(profile.loginTime)" disabled></el-input>
          </el-form-item>
          <el-form-item label="最近登录 IP">
            <el-input :value="profile.loginIp || '--'" disabled></el-input>
          </el-form-item>
          <el-form-item label="注册 IP">
            <el-input :value="profile.registerIp || '--'" disabled></el-input>
          </el-form-item>
        </el-form>
      </article>
    </section>

    <section class="info-grid">
      <article class="dashboard-panel">
        <div class="panel-heading">
          <div>
            <h2>修改密码</h2>
            <p>修改后下次登录请使用新密码，当前邮箱仍保持不变。</p>
          </div>
        </div>
        <el-form
          ref="passwordForm"
          :model="passwordForm"
          :rules="passwordRules"
          label-width="110px"
          class="profile-form"
        >
          <el-form-item label="当前密码" prop="currentPassword">
            <el-input
              v-model="passwordForm.currentPassword"
              type="password"
              show-password
              autocomplete="current-password"
            ></el-input>
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input
              v-model="passwordForm.newPassword"
              type="password"
              show-password
              autocomplete="new-password"
            ></el-input>
          </el-form-item>
          <el-form-item label="确认新密码" prop="confirmPassword">
            <el-input
              v-model="passwordForm.confirmPassword"
              type="password"
              show-password
              autocomplete="new-password"
            ></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" round :loading="changingPassword" @click="submitPasswordChange">
              修改密码
            </el-button>
            <el-button @click="resetPasswordForm">重置</el-button>
          </el-form-item>
        </el-form>
      </article>

      <article class="dashboard-panel">
        <h3>安全提示</h3>
        <p>如果最近登录时间或登录 IP 不是你本人的操作，建议尽快修改密码并重新登录常用设备。</p>
      </article>
    </section>
  </section>
</template>

<script>
import { changePassword, fetchProfile } from '../../api/profile';

const LOGIN_STORAGE_KEY = 'codex-api-login-user';

function createProfile() {
  return {
    email: '',
    status: 1,
    registerTime: null,
    loginTime: null,
    registerIp: '',
    loginIp: ''
  };
}

function createPasswordForm() {
  return {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
}

export default {
  name: 'ProfileView',
  data() {
    const self = this;
    return {
      loadingProfile: false,
      changingPassword: false,
      profile: createProfile(),
      passwordForm: createPasswordForm(),
      passwordRules: {
        currentPassword: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '新密码至少 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          {
            validator(rule, value, callback) {
              if (value !== self.passwordForm.newPassword) {
                callback(new Error('两次输入的新密码不一致'));
                return;
              }
              callback();
            },
            trigger: 'blur'
          }
        ]
      }
    };
  },
  created() {
    this.loadProfile();
  },
  methods: {
    async loadProfile() {
      this.loadingProfile = true;
      try {
        const { data } = await fetchProfile();
        if (!data || data.code !== 0 || !data.data) {
          throw new Error((data && data.message) || 'PROFILE_LOAD_FAILED');
        }
        this.profile = Object.assign(createProfile(), data.data);
        if (this.profile.email) {
          window.localStorage.setItem(LOGIN_STORAGE_KEY, this.profile.email);
        }
      } catch (error) {
        this.profile = createProfile();
        this.$message.error(error.message || '个人资料加载失败');
      } finally {
        this.loadingProfile = false;
      }
    },
    submitPasswordChange() {
      this.$refs.passwordForm.validate(async valid => {
        if (!valid) {
          return;
        }
        this.changingPassword = true;
        try {
          const { data } = await changePassword(
            this.passwordForm.currentPassword,
            this.passwordForm.newPassword
          );
          if (!data || data.code !== 0) {
            throw new Error((data && data.message) || 'PASSWORD_CHANGE_FAILED');
          }
          this.$message.success('密码修改成功');
          this.resetPasswordForm();
        } catch (error) {
          this.$message.error(error.message || '密码修改失败');
        } finally {
          this.changingPassword = false;
        }
      });
    },
    resetPasswordForm() {
      this.passwordForm = createPasswordForm();
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.resetFields();
      }
    },
    formatDateTime(value) {
      if (!value) {
        return '--';
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const day = `${date.getDate()}`.padStart(2, '0');
      const hours = `${date.getHours()}`.padStart(2, '0');
      const minutes = `${date.getMinutes()}`.padStart(2, '0');
      const seconds = `${date.getSeconds()}`.padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  }
};
</script>
